import type { ClientBook } from "@/features/intelligence/lib/client-id-resolve";
import { HANDLER_BUDGET_MS } from "@/features/intelligence/lib/frontend-tool-outcome";
import {
  clientIdentitiesInFlight,
  clientIdentitiesNow,
  clientRowsEverLoaded,
  clientRowsNow,
  ensureClientRows,
  type ClientRowsState,
} from "@/features/clients/lib/row-store";
import type { ClientListItem, ClientListResponse } from "@/domain/clients/list-types";

const CLIENT_BOOK_WAIT_MS = HANDLER_BUDGET_MS / 2;

function clientBookOf(
  state: ClientRowsState,
  everLoaded: boolean,
  identities: ClientListResponse | null,
): ClientBook {
  if (identities !== null) {
    return bookOf(identities.clients, identities.endedIds, identities.endedIcIds);
  }
  if (everLoaded && state.rows !== null) {
    return bookOf(state.rows, state.endedIds, state.endedIcIds);
  }
  return { live: null, endedIndividualIds: state.endedIds, endedIcIds: state.endedIcIds };
}

function bookOf(
  rows: readonly ClientListItem[],
  endedIds: readonly string[],
  endedIcIds: readonly string[],
): ClientBook {
  return {
    live: rows.map((r) => ({
      kind: r.kind,
      id: r.id,
      icId: r.icId ?? null,
      partyId: r.partyId,
      name: r.name,
    })),
    endedIndividualIds: endedIds,
    endedIcIds,
  };
}

export interface ClientBookSource {
  now: () => ClientBook;
  inFlight: () => Promise<void> | null;
  warm: () => void;
}

export const LIVE_CLIENT_BOOK: ClientBookSource = {
  now: () => clientBookOf(clientRowsNow(), clientRowsEverLoaded(), clientIdentitiesNow()),
  inFlight: () => clientIdentitiesInFlight(),
  warm: () => void ensureClientRows(),
};

export async function clientBookWhenLoaded(
  source: ClientBookSource = LIVE_CLIENT_BOOK,
  budgetMs: number = CLIENT_BOOK_WAIT_MS,
  setTimer: (fn: () => void, ms: number) => unknown = (fn, ms) => setTimeout(fn, ms),
  clearTimer: (handle: unknown) => void = (h) => clearTimeout(h as never),
): Promise<ClientBook> {
  const held = source.now();
  if (held.live !== null) return held;

  const flight = source.inFlight();
  if (flight === null) {
    source.warm();
    return held;
  }

  let handle: unknown = null;
  const expiry = new Promise<void>((resolve) => {
    handle = setTimer(() => resolve(), budgetMs);
  });
  try {
    await Promise.race([flight.catch(() => undefined), expiry]);
  } finally {
    if (handle !== null) clearTimer(handle);
  }
  return source.now();
}
